import { useRef } from "react";
import { motion } from "framer-motion";
import { BadgeDollarSign, CalendarClock, HeartHandshake, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";
import { PantherSpotter, useCrawlT, easeIn, easeOut, easeInOut } from "@/components/landing/PantherSpotter";
import { useTransform } from "framer-motion";

const PERKS = [
  {
    icon: BadgeDollarSign,
    title: "UPFRONT & FAIR",
    copy: "The price is on this very page. Wild concept, we know.",
  },
  {
    icon: CalendarClock,
    title: "LIVE AVAILABILITY",
    copy: "Pick your own rock-up time online. Nobody else in Perth does it.",
  },
  {
    icon: ShieldCheck,
    title: "LIFETIME LABOUR",
    copy: "A workmanship warranty that lasts as long as you do.",
  },
  {
    icon: HeartHandshake,
    title: "HAPPINESS GUARANTEE",
    copy: "Not happy? We're not done. Cliché, sure. True, absolutely.",
  },
];

// line i: chase out 5+0.15i → 7.5+0.15i | hidden | bounce in 8.6+0.4i → 9.55+0.4i | home
const lineX = (s, i) => {
  const oS = 5 + 0.15 * i, oE = 7.5 + 0.15 * i, iS = 8.6 + 0.4 * i, iM = 9.3 + 0.4 * i, iE = 9.55 + 0.4 * i;
  if (s < oS) return 0;
  if (s < oE) return 108 * easeIn((s - oS) / (oE - oS));
  if (s < iS) return -75;
  if (s < iM) return -75 + 77.5 * easeOut((s - iS) / (iM - iS));
  if (s < iE) return 2.5 - 2.5 * easeInOut((s - iM) / (iE - iM));
  return 0;
};
const lineRot = (s, i) => {
  const oS = 5 + 0.15 * i, oE = 7.5 + 0.15 * i, iS = 8.6 + 0.4 * i, iM = 9.3 + 0.4 * i, iE = 9.55 + 0.4 * i;
  if (s < oS) return 0;
  if (s < oE) return 5 * easeIn((s - oS) / (oE - oS));
  if (s < iS) return -5;
  if (s < iM) return -5 + 7.5 * easeOut((s - iS) / (iM - iS));
  if (s < iE) return 2.5 - 2.5 * easeInOut((s - iM) / (iE - iM));
  return 0;
};
const lineOpacity = (s, i) => (s >= 7.5 + 0.15 * i && s <= 8.6 + 0.4 * i ? 0 : 1);

const SlideLine = ({ children, i, t }) => {
  const x = useTransform(t, (s) => `${lineX(s, i)}vw`);
  const rotate = useTransform(t, (s) => lineRot(s, i));
  const opacity = useTransform(t, (s) => lineOpacity(s, i));
  return (
    <motion.span className="block" style={{ x, rotate, opacity }}>
      {children}
    </motion.span>
  );
};

export const WhyUs = () => {
  const sectionRef = useRef(null);
  const t = useCrawlT(sectionRef);
  return (
  <section ref={sectionRef} data-testid="why-us-section" className="pt-5 pb-10 lg:py-32 bg-tlime overflow-hidden">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-8">
        <div className="flex-1 relative z-10">
          <Reveal>
            <p className="font-bold text-xs tracking-[0.3em] text-brand uppercase mb-4">Chapter 01 — Why us? That's easy…</p>
          </Reveal>
          <h2 className="font-bebas text-5xl sm:text-6xl lg:text-7xl text-navy leading-[0.95]">
            <SlideLine i={0} t={t}>NO GAMES.</SlideLine>
            <SlideLine i={1} t={t}>NO GUESSWORK.</SlideLine>
            <SlideLine i={2} t={t}>
              <span className="text-brand">JUST FOUND & FIXED.</span>
            </SlideLine>
          </h2>
        </div>
        <PantherSpotter t={t} className="relative w-full max-w-[620px] mx-auto lg:mx-0 lg:-ml-48 lg:w-[53%] lg:max-w-none aspect-[16/9] lg:-my-10 shrink-0 -order-1 lg:order-none -mt-12 -mb-4 lg:mt-0 lg:mb-0" />
      </div>
      <div className="mt-6 lg:mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {PERKS.map((perk, i) => (
          <Reveal key={perk.title} delay={i * 0.08}>
            <div
              data-testid={`perk-card-${i}`}
              className="group h-full rounded-3xl border border-border bg-white p-8 shadow-sm hover:shadow-xl hover:shadow-brand/10 hover:-translate-y-1.5 hover:border-brand/50 transition-[transform,box-shadow,border-color] duration-300"
            >
              <div className="w-14 h-14 rounded-2xl bg-brand-light flex items-center justify-center mb-6 group-hover:bg-brand transition-colors duration-300">
                <perk.icon className="w-7 h-7 text-brand group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="font-bebas text-2xl tracking-wide text-navy mb-3">{perk.title}</h3>
              <p className="text-sm text-navy/60 leading-relaxed">{perk.copy}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <div className="mt-5 lg:mt-8 mx-auto w-full lg:w-1/2">
          <video
            data-testid="why-us-video"
            poster="/video/leak-detection-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            className="w-full aspect-video object-cover rounded-3xl border border-border bg-white shadow-sm"
          >
            <source src="/video/leak-detection.mp4" type="video/mp4" />
            <source src="/video/leak-detection.webm" type="video/webm" />
          </video>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
